// apps/client/src/services/chat.ts
import { ChatStreamRequest, ComplianceProgress } from '@avanzu/types';
import { apiClient } from './apiClient';

/**
 * Stream chat messages from the AI assistant
 * Uses Server-Sent Events (SSE) to receive streaming responses
 */
export const streamChat = async (
  organisationId: string,
  request: ChatStreamRequest,
  onChunk: (chunk: string) => void,
  onComplianceUpdate?: (progress: ComplianceProgress) => void
): Promise<void> => {
  try {
    console.log('[streamChat] Starting stream request');
    const response = await apiClient.post(
      `/organisations/${organisationId}/projects/chat/stream`,
      request
    );

    console.log('[streamChat] Response status:', response.status);
    console.log('[streamChat] Response headers:', Object.fromEntries(response.headers.entries()));

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.error('[streamChat] Error response:', errorData);
      throw new Error(errorData.message || 'Failed to stream chat');
    }

    const reader = response.body?.getReader();
    if (!reader) {
      throw new Error('Response body is not readable');
    }

    const decoder = new TextDecoder();
    let buffer = '';

    while (true) {
      const { done, value } = await reader.read();
      if (done) {
        console.log('[streamChat] Stream complete');
        break;
      }

      // Decode the chunk
      const chunk = decoder.decode(value, { stream: true });
      console.log('[streamChat] Received chunk length:', chunk.length);
      console.log('[streamChat] Chunk preview:', chunk.substring(0, 200));
      buffer += chunk;

      // Process complete SSE events
      const lines = buffer.split('\n');
      buffer = lines.pop() || ''; // Keep incomplete line in buffer

      let currentEvent: string | null = null;
      for (const line of lines) {
        // Check for event type
        if (line.startsWith('event: ')) {
          currentEvent = line.slice(7); // Remove 'event: ' prefix
          continue;
        }

        if (line.startsWith('data: ')) {
          const data = line.slice(6); // Remove 'data: ' prefix

          // Skip ping events
          if (data === 'ping') continue;

          try {
            // Handle compliance_update events
            if (currentEvent === 'compliance_update') {
              const progress = JSON.parse(data) as ComplianceProgress;
              console.log('[streamChat] Received compliance update:', progress);
              onComplianceUpdate?.(progress);
              currentEvent = null;
              continue;
            }

            const event = JSON.parse(data);
            console.log('[streamChat] Parsed event:', event.type, event);

            // Handle content_block_delta events (these contain the actual text)
            if (event.type === 'content_block_delta' && event.delta?.text) {
              console.log('[streamChat] Received text:', event.delta.text);
              onChunk(event.delta.text);
            }

            // Handle content_block_start to see if there's content
            if (event.type === 'content_block_start') {
              console.log('[streamChat] Content block start:', event);
            }

            // Handle message_delta with stop_reason
            if (event.type === 'message_delta' && event.delta?.stop_reason) {
              console.log('[streamChat] Stop reason:', event.delta.stop_reason);
              if (event.delta.stop_reason === 'refusal') {
                console.error('[streamChat] Claude refused to respond');
              }
            }

            // Handle errors
            if (event.type === 'error') {
              console.error('[streamChat] Stream error:', event.error);
              throw new Error(event.error?.message || 'Stream error');
            }
          } catch (error) {
            // Ignore JSON parse errors for non-JSON lines
            if (data !== '[DONE]') {
              console.warn('[streamChat] Failed to parse SSE event:', data);
            }
          }
          currentEvent = null;
        }
      }
    }
  } catch (error) {
    throw new Error(
      error instanceof Error ? error.message : 'Failed to stream chat'
    );
  }
};
